/**
 * AudienceParcoursStubPage
 * ─────────────────────────────────────────────────────────────────
 * Variante tone-aware de ParcoursStubPage utilisable par les audiences
 * B2B/B2G/RGE : pro, entreprise, tertiaire, industrie, collectivités,
 * copropriétés, artisans.
 *
 * Sert aussi de template pour :
 *  - les pages "travaux" (operations BAR/BAT/IND par secteur)
 *  - les pages "patrimoine" (collectivités)
 *  - les pages "process AG" (copropriétés)
 *  - les pages "catalogue" (artisans)
 *
 * Refonte 2026 : aligné sur le niveau du kit `audience/particulier`
 *  - hero pleine largeur (fini le `Container narrow` centré), fond
 *    décoratif tonal (dot-grid + spot-glow + 2 halos), animations Reveal
 *  - carte "Indicateur clé" en side-slot du hero
 *  - bandeau de repères chiffrés (CountUp)
 *  - cards riches (hairline top + hover lift) + stepper numéroté
 *  - FAQ éditoriale 2 colonnes
 *  - CTA final immersif (rounded-[32px] mesh-dark + halos tonals)
 */

import { ArrowRight, CheckCircle2 } from 'lucide-react';
import type { AudienceId } from '@shared/constants/audiences';
import { Container } from '../layout/Container';
import { Button } from '../ui/button';
import { Reveal } from '../motion/Reveal';
import { StaggerGrid, StaggerItem } from '../motion/StaggerGrid';
import { CountUp } from '../motion/CountUp';
import { BreadcrumbListLd } from '../seo/jsonld/BreadcrumbList';
import { FAQPageLd } from '../seo/jsonld/FAQPage';
import { getIcon } from '../../lib/lucide-icons';
import { cn } from '../../lib/cn';
import type { ParcoursStub } from '../../config/parcours-particuliers';
import {
  getStubTokens,
  getAudienceBreadcrumbLabel,
  SITE_URL,
  type StubToneTokens,
} from '../audience/_shared/tone-tokens';
import { getAudienceById } from '@shared/constants/audiences';

export interface AudienceCrossLink {
  label: string;
  href: string;
  iconName: string;
  hint: string;
}

interface Props {
  parcours: ParcoursStub;
  audienceId: AudienceId;
  /** Segment d'URL après l'audience ("operations", "travaux", "patrimoine"…) */
  segment: string;
  /** Libellé visible du segment dans le breadcrumb */
  segmentLabel: string;
  /** Cross-links optionnels (3 max) affichés avant la FAQ */
  crossLinks?: AudienceCrossLink[];
  /** Libellé du bouton "voir toutes" sous les cross-links */
  crossLinksAllLabel?: string;
  /** URL "voir toutes" pour les cross-links */
  crossLinksAllHref?: string;
  /**
   * Override du CTA final (eyebrow + titre + description).
   * Utile pour les pages qui ne sont pas un "diagnostic" classique
   * (process AG, PPT, résolution juridique, etc.).
   */
  ctaFinalOverride?: {
    eyebrow?: string;
    title?: string;
    description?: string;
    /** Override du CTA primaire — par défaut on prend celui de parcours */
    primary?: { label: string; href: string };
    /** Override du CTA secondaire — par défaut "Parler à un conseiller" */
    secondary?: { label: string; href: string };
    /** Override des bullets de réassurance (3 max) */
    bullets?: string[];
  };
}

/** Carte "Indicateur clé" tone-aware — side-slot du hero. */
function ParcoursBadgeCard({
  parcours,
  t,
}: {
  parcours: ParcoursStub;
  t: StubToneTokens;
}) {
  return (
    <Reveal delay={0.12}>
      <div
        className={cn(
          'relative overflow-hidden rounded-[28px] border border-stone-200 bg-paper p-7 shadow-[0_2px_28px_-10px_rgba(10,10,10,.14)] ring-1 ring-inset',
          t.sideCardRing,
        )}
      >
        <div aria-hidden className={cn('absolute inset-x-0 top-0 h-px', t.cardTopHairline)} />
        <div className="relative">
          <p className="eyebrow text-stone-500">Indicateur clé</p>
          <p
            className={cn(
              'mt-2 font-display text-[2rem] font-black leading-none tracking-tight',
              t.gradientText,
            )}
          >
            {parcours.badge}
          </p>
          <p className="mt-2 text-[13px] leading-relaxed text-stone-500">{parcours.badgeLabel}</p>

          <Button asChild variant={t.buttonVariant} className={cn('mt-6 w-full', t.ctaShadow)}>
            <a href={parcours.ctaHref}>
              {parcours.ctaLabel}
              <ArrowRight className="h-4 w-4" />
            </a>
          </Button>
          <p className="mt-3 text-center text-[11px] text-stone-400">
            Gratuit · sans engagement · 60 secondes
          </p>
        </div>
      </div>
    </Reveal>
  );
}

export function AudienceParcoursStubPage({
  parcours,
  audienceId,
  segment,
  segmentLabel,
  crossLinks,
  crossLinksAllLabel,
  crossLinksAllHref,
  ctaFinalOverride,
}: Props) {
  const HeroIcon = getIcon(parcours.iconName);
  const t = getStubTokens(audienceId);
  const audience = getAudienceById(audienceId);
  const audienceHref = audience?.href ?? '/';
  const audienceLabel = getAudienceBreadcrumbLabel(audienceId);
  const segmentHref = `${audienceHref}/${segment}`;
  const detailHref = `${audienceHref}/${segment}/${parcours.slug}`;
  const stepCount = parcours.steps.length;

  const kpi = [
    {
      numericValue: stepCount,
      suffix: ' étapes',
      label: 'encadrées de A à Z',
      source: 'Process clair, sans surprise',
    },
    {
      value: '60 sec',
      label: 'pour évaluer votre projet',
      source: 'Diagnostic gratuit, 100 % en ligne',
    },
    {
      value: '0 €',
      label: 'pour faire votre diagnostic',
      source: 'Sans inscription ni engagement',
    },
  ];

  return (
    <>
      <BreadcrumbListLd
        items={[
          { name: 'Accueil', url: `${SITE_URL}/` },
          { name: audienceLabel, url: `${SITE_URL}${audienceHref}` },
          { name: segmentLabel, url: `${SITE_URL}${segmentHref}` },
          { name: parcours.title, url: `${SITE_URL}${detailHref}` },
        ]}
      />
      <FAQPageLd items={parcours.faq.map((f) => ({ question: f.question, answer: f.answer }))} />

      {/* ── Hero pleine largeur ── */}
      <section className="relative overflow-hidden bg-paper pt-12 pb-16 md:pt-20 md:pb-24">
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="absolute inset-0 dot-grid-masked" />
          <div className={t.spotGlow} />
          <div className={t.heroHaloTopRight} />
          <div className={t.heroHaloBottomLeft} />
        </div>

        <Container className="relative">
          {/* Fil d'Ariane visible */}
          <nav aria-label="Fil d'Ariane" className="mb-8 text-[12px] text-stone-500">
            <a href={audienceHref} className={cn('transition-colors', t.linkColor)}>
              {audienceLabel}
            </a>
            <span className="mx-1.5 text-stone-300">/</span>
            <a href={segmentHref} className={cn('transition-colors', t.linkColor)}>
              {segmentLabel}
            </a>
            <span className="mx-1.5 text-stone-300">/</span>
            <span className="font-medium text-stone-700">{parcours.title}</span>
          </nav>

          <div className="grid items-start gap-10 lg:grid-cols-[1.1fr_1fr] lg:gap-14">
            <div>
              <Reveal>
                <span
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em]',
                    t.eyebrowChip,
                  )}
                >
                  <HeroIcon className="h-3 w-3" strokeWidth={2} />
                  {parcours.eyebrow}
                </span>
              </Reveal>

              <Reveal delay={0.06}>
                <h1 className="mt-6 max-w-2xl text-balance font-display text-[clamp(2rem,4.5vw,3.25rem)] font-bold leading-[1.04] tracking-[-0.03em] text-ink">
                  {parcours.title}
                </h1>
              </Reveal>

              <Reveal delay={0.14}>
                <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-stone-600">
                  {parcours.lead}
                </p>
              </Reveal>

              <Reveal delay={0.22}>
                <div className="mt-7 flex flex-wrap items-center gap-2.5">
                  <Button asChild size="lg" variant={t.buttonVariant} className={cn('group', t.ctaShadow)}>
                    <a href={parcours.ctaHref}>
                      {parcours.ctaLabel}
                      <ArrowRight className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover/btn:translate-x-0.5" />
                    </a>
                  </Button>
                  {parcours.secondaryCta && (
                    <Button asChild size="lg" variant="secondary">
                      <a href={parcours.secondaryCta.href}>{parcours.secondaryCta.label}</a>
                    </Button>
                  )}
                </div>
              </Reveal>

              <Reveal delay={0.32}>
                <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] text-stone-600">
                  {(parcours.heroBullets ?? [
                    'Diagnostic gratuit · sans engagement',
                    'Sources réglementaires citées',
                    'Aucune relance commerciale',
                  ]).map((b) => (
                    <span key={b} className="inline-flex items-center gap-1.5">
                      <CheckCircle2 className={cn('h-3 w-3', t.checkColor)} strokeWidth={2.2} />
                      {b}
                    </span>
                  ))}
                </div>
              </Reveal>

              <Reveal delay={0.42}>
                <p className="mt-3 inline-flex items-center gap-1.5 font-mono text-[11px] text-stone-500">
                  <span className="inline-block h-1 w-1 rounded-full bg-stone-400" />
                  {parcours.badgeLabel}
                </p>
              </Reveal>
            </div>

            <ParcoursBadgeCard parcours={parcours} t={t} />
          </div>
        </Container>
      </section>

      {/* ── Bandeau de repères ── */}
      <section className="border-y border-stone-200/60 bg-stone-50/50 py-10">
        <Container>
          <StaggerGrid className="grid gap-px overflow-hidden rounded-xl bg-stone-200/60 sm:grid-cols-3">
            {kpi.map((k) => (
              <StaggerItem key={k.label} className="bg-paper p-5">
                <p className="metric-xl">
                  {k.numericValue !== undefined ? (
                    <CountUp end={k.numericValue} suffix={k.suffix} />
                  ) : (
                    k.value
                  )}
                </p>
                <p className="mt-1.5 text-xs text-stone-700">{k.label}</p>
                {k.source && <p className="mt-0.5 text-[10px] text-stone-400">{k.source}</p>}
              </StaggerItem>
            ))}
          </StaggerGrid>
        </Container>
      </section>

      {/* ── Points clés ── */}
      <section className="py-20 md:py-28">
        <Container>
          <Reveal>
            <div className="mx-auto mb-10 max-w-2xl text-center">
              <p className="eyebrow text-stone-500">L’essentiel</p>
              <h2 className="mt-3 text-balance font-display text-[clamp(1.75rem,3.5vw,2.5rem)] font-bold tracking-tight text-ink">
                Ce qu’il faut savoir avant de se lancer
              </h2>
            </div>
          </Reveal>

          <StaggerGrid className="grid gap-4 sm:grid-cols-2">
            {parcours.keyPoints.map((point) => {
              const Icon = getIcon(point.iconName);
              return (
                <StaggerItem key={point.label}>
                  <div className="group relative h-full overflow-hidden rounded-2xl border border-stone-200 bg-paper p-6 shadow-[0_1px_3px_rgba(10,10,10,.04)] transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-stone-300 hover:shadow-[0_12px_36px_-12px_rgba(10,10,10,.10)]">
                    <div aria-hidden className={cn('absolute inset-x-0 top-0 h-px', t.cardTopHairline)} />
                    <span
                      className={cn(
                        'mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl ring-1 ring-inset transition-transform duration-200 ease-out group-hover:scale-105',
                        t.iconChipBg,
                        t.iconChipText,
                        t.iconChipRing,
                      )}
                    >
                      <Icon className="h-5 w-5" strokeWidth={1.7} />
                    </span>
                    <p className="font-display text-base font-semibold text-ink">{point.label}</p>
                    <p className="mt-1.5 text-[13.5px] leading-relaxed text-stone-600">
                      {point.description}
                    </p>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerGrid>
        </Container>
      </section>

      {/* ── Déroulement (stepper) ── */}
      <section className="border-t border-stone-100 bg-gradient-to-b from-stone-50/60 to-paper py-20 md:py-28">
        <Container>
          <Reveal>
            <div className="mb-10 max-w-3xl">
              <p className="eyebrow text-stone-500">Déroulement</p>
              <h2 className="mt-3 text-balance font-display text-[clamp(1.75rem,3.5vw,2.5rem)] font-bold tracking-tight text-ink">
                Comment ça se passe
              </h2>
              <p className="mt-4 text-[14.5px] leading-relaxed text-stone-600">
                Le déroulement présenté ci-dessous est indicatif. Chaque situation est différente :
                caractéristiques du projet, contexte réglementaire, opportunités d’aides spécifiques
                à votre territoire.
              </p>
            </div>
          </Reveal>

          <Reveal>
            <ol className="space-y-3">
              {parcours.steps.map((step, idx) => (
                <li
                  key={step.title}
                  className="flex items-start gap-4 rounded-2xl border border-stone-200 bg-paper p-5 transition-all duration-200 ease-out hover:border-stone-300 hover:shadow-[0_8px_24px_-12px_rgba(10,10,10,.08)]"
                >
                  <span
                    className={cn(
                      'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-[14px] font-bold tabular-nums ring-1 ring-inset',
                      t.iconChipBg,
                      t.iconChipText,
                      t.iconChipRing,
                    )}
                  >
                    {idx + 1}
                  </span>
                  <div className="min-w-0">
                    <p className="text-[15px] font-semibold text-ink">{step.title}</p>
                    <p className="mt-1 text-[13px] leading-relaxed text-stone-600">{step.description}</p>
                  </div>
                </li>
              ))}
            </ol>
          </Reveal>

          <div className="mt-8 flex justify-center">
            <Button asChild size="lg" variant={t.buttonVariant} className={cn('group', t.ctaShadow)}>
              <a href={parcours.ctaHref}>
                {parcours.ctaLabel}
                <ArrowRight className="h-4 w-4 transition-transform duration-150 ease-out group-hover/btn:translate-x-0.5" />
              </a>
            </Button>
          </div>
        </Container>
      </section>

      {/* ── Cross-link aides applicables (optionnel) ── */}
      {crossLinks && crossLinks.length > 0 && (
        <section className="py-20 md:py-28">
          <Container>
            <Reveal>
              <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
                <div className="max-w-2xl">
                  <p className="eyebrow text-stone-500">Aides mobilisables</p>
                  <h2 className="mt-3 text-balance font-display text-[clamp(1.75rem,3.5vw,2.5rem)] font-bold tracking-tight text-ink">
                    Comment financer ce projet ?
                  </h2>
                </div>
                {crossLinksAllHref && crossLinksAllLabel && (
                  <a
                    href={crossLinksAllHref}
                    className={cn(
                      'hidden shrink-0 text-[13px] font-semibold hover:underline md:inline-flex',
                      t.linkColor,
                    )}
                  >
                    {crossLinksAllLabel} →
                  </a>
                )}
              </div>
            </Reveal>

            <StaggerGrid className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {crossLinks.map((cta) => {
                const Icon = getIcon(cta.iconName);
                return (
                  <StaggerItem key={cta.href}>
                    <a
                      href={cta.href}
                      className="group flex h-full items-start gap-3 rounded-2xl border border-stone-200 bg-paper p-5 transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-stone-300 hover:shadow-[0_12px_36px_-12px_rgba(10,10,10,.10)]"
                    >
                      <span
                        className={cn(
                          'mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ring-1 ring-inset',
                          t.iconChipBg,
                          t.iconChipText,
                          t.iconChipRing,
                        )}
                      >
                        <Icon className="h-4 w-4" strokeWidth={1.7} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-[14px] font-semibold text-ink">{cta.label}</p>
                        <p className="mt-1 text-[12px] leading-relaxed text-stone-500">{cta.hint}</p>
                      </div>
                      <ArrowRight
                        className="mt-1 h-3.5 w-3.5 shrink-0 text-stone-300 transition-transform duration-150 ease-out group-hover:translate-x-0.5 group-hover:text-stone-600"
                        strokeWidth={2.2}
                      />
                    </a>
                  </StaggerItem>
                );
              })}
            </StaggerGrid>
            {crossLinksAllHref && crossLinksAllLabel && (
              <div className="mt-6 flex justify-center md:hidden">
                <Button asChild variant="ghost">
                  <a href={crossLinksAllHref}>{crossLinksAllLabel}</a>
                </Button>
              </div>
            )}
          </Container>
        </section>
      )}

      {/* ── FAQ éditoriale 2 colonnes ── */}
      <section className={cn('py-20 md:py-28', crossLinks && crossLinks.length > 0 && 'border-t border-stone-100 bg-gradient-to-b from-stone-50/60 to-paper')}>
        <Container>
          <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
            <Reveal>
              <div className="lg:sticky lg:top-28 lg:self-start">
                <p className="eyebrow text-stone-500">FAQ {parcours.shortLabel}</p>
                <h2 className="mt-3 text-balance font-display text-[clamp(1.75rem,3.5vw,2.5rem)] font-bold tracking-tight text-ink">
                  Vos questions sur {parcours.shortLabel}
                </h2>
                <p className="mt-4 text-[14.5px] leading-relaxed text-stone-600">
                  Une question reste sans réponse ?{' '}
                  <a
                    href={`/contact?source=${audienceId}`}
                    className={cn('font-semibold hover:underline', t.linkColor)}
                  >
                    Écrivez-nous →
                  </a>
                </p>
              </div>
            </Reveal>

            <div className="divide-y divide-stone-100 overflow-hidden rounded-2xl border border-stone-200 bg-paper">
              {parcours.faq.map((item) => (
                <details
                  key={item.question}
                  className="group p-5 transition-colors duration-150 ease-out hover:bg-stone-50/60"
                >
                  <summary className="flex cursor-pointer items-start justify-between gap-4 text-[15px] font-semibold text-ink marker:hidden">
                    <span>{item.question}</span>
                    <span className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-stone-100 text-stone-500 transition-transform duration-150 ease-out group-open:rotate-45">
                      +
                    </span>
                  </summary>
                  <p className="mt-3 text-[14px] leading-relaxed text-stone-600">{item.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* ── CTA final immersif ── */}
      <section className="relative bg-paper pb-20 pt-4">
        <Container>
          <div className="relative overflow-hidden rounded-[32px] bg-ink px-6 py-14 sm:px-12 sm:py-16 md:px-16 md:py-20">
            <div aria-hidden className="pointer-events-none absolute inset-0 bg-mesh-dark opacity-90" />
            <div aria-hidden className={t.ctaHaloTopRight} />
            <div aria-hidden className={t.ctaHaloBottomLeft} />
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 opacity-[0.06]"
              style={{
                backgroundImage:
                  'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)',
                backgroundSize: '48px 48px',
                maskImage: 'radial-gradient(ellipse 80% 60% at 50% 50%, black 30%, transparent 80%)',
                WebkitMaskImage: 'radial-gradient(ellipse 80% 60% at 50% 50%, black 30%, transparent 80%)',
              }}
            />
            <div aria-hidden className="pointer-events-none absolute inset-0 bg-noise opacity-[0.04]" />

            <Reveal>
              <div className="relative text-center">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-ivory-50 backdrop-blur-md">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className={cn('absolute inline-flex h-full w-full animate-glow-pulse rounded-full opacity-70', t.darkDotClass)} />
                    <span className={cn('relative inline-flex h-1.5 w-1.5 rounded-full', t.darkDotClass)} />
                  </span>
                  {ctaFinalOverride?.eyebrow ?? 'Diagnostic gratuit · 60 sec'}
                </span>

                <h2 className="mt-5 text-balance font-display text-[clamp(1.75rem,3.5vw,2.75rem)] font-bold leading-[1.05] tracking-tight text-ivory-50">
                  {ctaFinalOverride?.title ? (
                    ctaFinalOverride.title
                  ) : (
                    <>
                      Étudions le potentiel de{' '}
                      <span className={t.headlineGradientDark}>votre projet</span>
                    </>
                  )}
                </h2>

                <p className="mx-auto mt-4 max-w-xl text-[15px] leading-relaxed text-ivory-50/70">
                  {ctaFinalOverride?.description ??
                    'Diagnostic gratuit en 60 secondes — sources officielles citées, sans inscription ni engagement.'}
                </p>

                <div className="mt-7 flex flex-wrap justify-center gap-3">
                  <Button asChild size="lg" variant={t.ctaButtonDarkVariant}>
                    <a href={ctaFinalOverride?.primary?.href ?? parcours.ctaHref}>
                      {ctaFinalOverride?.primary?.label ?? parcours.ctaLabel}
                      <ArrowRight className="h-4 w-4" />
                    </a>
                  </Button>
                  <Button asChild size="lg" variant="dark-outline">
                    <a href={ctaFinalOverride?.secondary?.href ?? `/contact?source=${audienceId}`}>
                      {ctaFinalOverride?.secondary?.label ?? 'Parler à un conseiller'}
                    </a>
                  </Button>
                </div>

                <div className="mx-auto mt-7 flex max-w-md flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[12px] text-ivory-50/60">
                  {(ctaFinalOverride?.bullets ?? [
                    'Sans engagement',
                    'Sources officielles',
                    'Aucune relance commerciale',
                  ]).map((b) => (
                    <span key={b} className="inline-flex items-center gap-1.5">
                      <CheckCircle2 className="h-3 w-3 text-accent-300/80" strokeWidth={2.2} />
                      {b}
                    </span>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </Container>
      </section>
    </>
  );
}
